import { useQuery } from "@tanstack/react-query";
import Immutable from "immutable";
import UserActions from "../../actions/UserActions";
import config from "../../config/app";
import AppConstants from "../../constants/AppConstants";
import { ACCOUNT } from "../../constants/QueryKeys";
import { getDaysBetween } from "../../utils/DateUtils";
import { queryClient, IMPERATIVE_CACHE_DEFAULTS } from "../../utils/QueryUtils";

/** @typedef {import("@/types/data/LoginResponse.mjs").LoginResponse} LoginResponse */
/** @typedef {import("@/types/data/Account.mjs").Account} Account */
/** @typedef {import("@/types/TypeUtils.mjs").ImmutableMapFrom<Account>} ImmutableAccountState */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

/** Account state lives in the ACCOUNT query cache. */

/**
 * Empty-cache shape, mirroring the old `UserStore` `model` it seeded `Immutable.fromJS(model)` with.
 * @type {Account}
 */
export const accountModel = {
  user_id: 0,
  first_name: "",
  last_name: "",
  facebook_id: "",
  business_name: "",
  email: "",
  token: null,
  messages: 0,
  loginError: null,
  isAdmin: false,
  domain: false,
  showTour: true,
  accountAgeDays: 0,
  isActive: false,
  status: AppConstants.USER_STATUS_TRIAL,
  statusExpiration: null,
  targetLaunchDate: null,
  hasLaunched: null,
  allowedBlogType: AppConstants.BLOG_TYPE_NONE,
  blogSetupNeeded: false,
  blogId: undefined,
  blogStatus: undefined,
  subscriptionService: undefined,
  actingContributor: undefined,
  intercomOpen: false,
};

const defaults = IMPERATIVE_CACHE_DEFAULTS;

// Register defaults on the shared client so non-hook callers inherit the same settings as `useAccount`.
queryClient.setQueryDefaults([ACCOUNT], defaults);

// ---- Cache primitives (plain-JS source of truth) ----

/**
 * One-shot, non-triggering read of the cached account payload (plain JS) for non-React callers.
 * @returns {Account | undefined}
 */
export const getAccountData = () =>
  /** @type {Account | undefined} */ (queryClient.getQueryData([ACCOUNT]));

/**
 * Writes the ACCOUNT cache entry for non-React callers.
 * @param {(prev: Account | undefined) => Account} updater
 */
export const setAccountData = (updater) => queryClient.setQueryData([ACCOUNT], updater);

/**
 * Shallow-merges `partial` into the cached account, seeding from `accountModel` when the cache is empty.
 * @param {Partial<Account>} partial
 */
export const mergeAccountData = (partial) =>
  setAccountData((prev) => /** @type {Account} */ ({ ...(prev ?? accountModel), ...partial }));

/**
 * Resets the account cache to the empty model`).
 */
export const resetAccount = () => setAccountData(() => ({ ...accountModel }));

// ---- Immutable read boundary (getState() parity) ----

// One conversion per cache write, shared by every consumer: the deep fromJS is
// memoized on the cached object's identity, so ~20 mounted subscribers get the
// same Map instead of 20 conversions (and 20 distinct identities) per write.
/** @type {Account | null | undefined} */
let lastAccountData;
/** @type {ImmutableAccountState | undefined} */
let lastAccountState;
/**
 * @param {Account | null | undefined} data
 * @returns {ImmutableAccountState}
 */
const toAccountState = (data) => {
  if (data !== lastAccountData || !lastAccountState) {
    lastAccountData = data;
    lastAccountState = Immutable.fromJS(data ?? accountModel);
  }
  return lastAccountState;
};

/**
 * Immutable-shaped read of the account, for the `getState()` consumers whose `.get()`/`.getIn()`/`.toJS()` usage fans out across components.
 * @returns {ImmutableAccountState}
 */
export const getAccountState = () => toAccountState(getAccountData());

// ---- Scalar getter-parity surface ----

/** @returns {number} the current user's id (0 before load, matching model). */
export const getUserId = () => getAccountData()?.user_id ?? accountModel.user_id;

/** @returns {boolean} whether the subscription is active. */
export const isActive = () => getAccountData()?.isActive ?? accountModel.isActive;

/** @returns {boolean} whether the logged-in user is an admin (impersonating). */
export const isAdmin = () => getAccountData()?.isAdmin ?? accountModel.isAdmin;

/** @returns {string} the allowed blog type (`BLOG_TYPE_NONE` before load). */
export const getBlogType = () => getAccountData()?.allowedBlogType ?? accountModel.allowedBlogType;

/**
 * Whether a contributor (not the account owner, not an admin, not demo mode) is the acting user.
 * @returns {boolean}
 */
export const isContributing = () => {
  const data = getAccountData() ?? accountModel;
  return !isAdmin() && !config.demoMode && data.user_id !== data.actingContributor?.id;
};

/**
 * Whether a third-party service is enabled: not an admin, the client config carries the key, and the window global exists.
 * @param {string} configName
 * @param {string} serviceFuncName
 * @returns {boolean}
 */
export const isServiceEnabled = (configName, serviceFuncName) =>
  !isAdmin() && Object.hasOwn(config, configName) && Object.hasOwn(window, serviceFuncName);

// ---- Account-state action seams (were UserStore.on* state mutations) ----

/** `UserActions.login` seam: clears the login error. */
export const handleLogin = () => {
  mergeAccountData({ loginError: null });
};

/**
 * `UserActions.updateConfiguration` seam.
 * @param {Object} configuration
 */
export const handleUpdateConfiguration = (configuration) => {
  mergeAccountData(configuration);
};

/**
 * `UserActions.userSubscribed` seam.
 * @param {{status: string, blogType: string, subscriptionService: string}} data
 */
export const handleUserSubscribed = ({ status, blogType, subscriptionService }) => {
  const update = {
    status,
    isActive: AppConstants.USER_STATUS_ACTIVE === status,
    allowedBlogType: blogType,
    subscriptionService,
  };

  if (getAccountData()?.blogId) {
    update.blogSetupNeeded = true;
  }

  mergeAccountData(update);
};

/**
 * Applies the login payload's `user` + derived fields into the cache.
 * @param {LoginResponse} data
 */
export const applyLoginData = (data) => {
  const { token, user } = data;
  const adminId = data.aid ?? 0;

  user.loginError = false;
  user.token = token;
  user.accountAgeDays = getDaysBetween({ start: user.create_date });
  if (AppConstants.TOUR_MAX_ACCOUNT_AGE < user.accountAgeDays) {
    user.showTour = false;
  }

  mergeAccountData(user);

  if (adminId > 0) {
    mergeAccountData({ isAdmin: true });
  }
};

/**
 * Merges an account-info payload, stripping the redundant `userId` key.
 * @param {Object} account
 */
export const applyAccountInfo = (account) => {
  const { userId: _userId, ...accountInfo } = account;
  mergeAccountData(accountInfo);
};

// ---- Module-level action -> cache-write bridges.
UserActions.login.listen(handleLogin);
UserActions.updateConfiguration.listen(handleUpdateConfiguration);
UserActions.userSubscribed.listen(handleUserSubscribed);

// ---- React hooks ----

/**
 * Subscribes a React component to the account (TanStack replacement for `useStoreState(UserStore, UserStore.getState)`).
 * @param {QueryHookOptions<Account | null>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ account: Account | null | undefined } & QueryResultRest<Account | null>} `null` only if the no-op queryFn ever runs.
 */
const useAccount = (options = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: [ACCOUNT],
    // Imperatively fed (enabled:false); the no-op queryFn silences TanStack's
    // "Missing queryFn" diagnostic on observer mount (see useSite.js).
    queryFn: () => getAccountData() ?? null,
    ...defaults,
    ...options,
  });
  return { account: data, ...rest };
};

/**
 * Immutable-shaped hook for the `getState()` component consumers.
 * @returns {ImmutableAccountState}
 */
export const useAccountState = () => {
  const { account } = useAccount();
  return toAccountState(account);
};

export default useAccount;
